import React, { useState } from "react";
import {
  Paper,
  Text,
  Group,
  Button,
  Stack,
  Collapse,
  Badge,
  ActionIcon,
  Modal,
  Alert,
} from "@mantine/core";
import {
  IconEdit,
  IconTrash,
  IconChevronDown,
  IconChevronUp,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { DepositConfiguration, SalonService } from "../../types/deposit";
import { formatDepositAmount } from "../../utils/depositUtils";
import DepositForm from "./DepositForm";

interface DepositCardProps {
  deposit: DepositConfiguration;
  services: SalonService[];
  onUpdate: (data: any) => void;
  onDelete: (id: string) => void;
  isLoading?: boolean;
}

const DepositCard = ({
  deposit,
  services,
  onUpdate,
  onDelete,
  isLoading = false,
}: DepositCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const associatedServices = services.filter((service) =>
    deposit.salon_service_ids.includes(service.id),
  );

  const handleUpdate = async (formData: any) => {
    try {
      setError(null);
      await onUpdate({
        ...formData,
        id: deposit.id,
        salon_id: deposit.id.split("-")[0], // Extract salon ID from deposit ID
      });
      setIsEditing(false);
    } catch (error: any) {
      console.error("Error updating deposit:", error);

      // Handle collision detection error
      if (
        error.message &&
        error.message.includes(
          "Collision detected with existing active configurations:",
        )
      ) {
        const cleanMessage = error.message.replace(" (Status Code: 400)", "");
        setError(cleanMessage);
      } else {
        setError("Failed to update deposit configuration. Please try again.");
      }
    }
  };

  const handleDelete = () => {
    onDelete(deposit.id);
    setShowDeleteModal(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setError(null);
  };

  if (isEditing) {
    return (
      <Paper p="md" withBorder>
        {error && (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            title="Error"
            color="red"
            mb="md"
            withCloseButton
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        <DepositForm
          initialData={{
            id: deposit.id,
            name: deposit.name,
            description: deposit.description || "",
            type: deposit.type,
            amount: deposit.amount,
            salon_service_ids: deposit.salon_service_ids,
            is_active: deposit.is_active,
          }}
          services={services}
          onSubmit={handleUpdate}
          onCancel={handleCancelEdit}
          isLoading={isLoading}
        />
      </Paper>
    );
  }

  return (
    <>
      <Paper p="md" withBorder>
        <Stack gap="sm">
          <Group justify="space-between" align="flex-start">
            <Stack gap="xs" style={{ flex: 1 }}>
              <Group gap="sm">
                <Text fw={500} size="lg">
                  {deposit.name}
                </Text>
                <Badge
                  color={deposit.is_active ? "green" : "gray"}
                  variant="light"
                >
                  {deposit.is_active ? "Active" : "Inactive"}
                </Badge>
              </Group>

              {deposit.description && (
                <Text size="sm" c="dimmed">
                  {deposit.description}
                </Text>
              )}

              <Text size="lg" fw={500} c="teal">
                {formatDepositAmount(deposit.type, deposit.amount)}
              </Text>
            </Stack>

            <Group gap="xs">
              <ActionIcon
                variant="light"
                color="teal"
                onClick={() => setIsEditing(true)}
                size="sm"
              >
                <IconEdit size={16} />
              </ActionIcon>

              <ActionIcon
                variant="light"
                color="red"
                onClick={() => setShowDeleteModal(true)}
                size="sm"
              >
                <IconTrash size={16} />
              </ActionIcon>

              <ActionIcon
                variant="light"
                color="gray"
                onClick={() => setIsExpanded(!isExpanded)}
                size="sm"
              >
                {isExpanded ? (
                  <IconChevronUp size={16} />
                ) : (
                  <IconChevronDown size={16} />
                )}
              </ActionIcon>
            </Group>
          </Group>

          <Collapse in={isExpanded}>
            <Stack gap="sm" pt="sm">
              <Text size="sm" fw={500}>
                Applies to Services:
              </Text>
              {associatedServices.length > 0 ? (
                <Group gap="xs">
                  {associatedServices.map((service) => (
                    <Badge key={service.id} variant="light" color="blue">
                      {service.name}
                    </Badge>
                  ))}
                </Group>
              ) : (
                <Text size="sm" c="dimmed">
                  No services selected
                </Text>
              )}
            </Stack>
          </Collapse>
        </Stack>
      </Paper>

      <Modal
        opened={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Deposit Configuration"
        centered
      >
        <Stack gap="md">
          <Alert
            icon={<IconAlertTriangle size={16} />}
            title="Are you sure?"
            color="red"
          >
            This will permanently delete the deposit configuration "
            {deposit.name}". This action cannot be undone.
          </Alert>

          <Group justify="flex-end" gap="sm">
            <Button variant="light" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDelete} loading={isLoading}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default DepositCard;
